import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { BRAND } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentReturn() {
  const path = window.location.pathname;
  const isSuccess = path.startsWith("/payment/success");
  const isCancel = path.startsWith("/payment/cancel");
  const [state, setState] = useState(isSuccess ? "polling" : isCancel ? "cancel" : "hidden");
  const [downloadUrl, setDownloadUrl] = useState("");

  useEffect(() => {
    if (!isSuccess) return;
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const res = await fetch(`${API}/payments/status/${sid}`);
        const data = await res.json();
        if (data.payment_status === "paid") { setDownloadUrl(data.download_url || ""); setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) { /* retry */ }
      if (tries >= 8) { setState("timeout"); return; }
      timer = setTimeout(poll, 1800);
    };
    poll();
    return () => clearTimeout(timer);
  }, [isSuccess]);

  if (state === "hidden") return null;

  const goHome = () => { window.location.href = "/"; };

  return (
    <div className="fixed inset-0 z-[10002] bg-[color:var(--void)] flex items-center justify-center px-6" data-testid="payment-return">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="glass-strong rounded-sm max-w-md w-full p-9 text-center"
      >
        <p className="font-display chrome-text text-2xl mb-8">{BRAND.name}</p>

        {state === "polling" && (
          <>
            <Loader2 size={34} className="mx-auto text-white/70 animate-spin mb-5" />
            <p className="track-label text-white/60">CONFIRMING PAYMENT</p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 size={40} className="mx-auto text-white mb-5" />
            <h2 className="font-display steel-text text-3xl mb-3" data-testid="payment-paid">THE MUSIC IS YOURS</h2>
            <p className="font-body text-white/55 text-sm mb-8">Payment confirmed. Thank you for owning a piece of the world of {BRAND.name}.</p>
            {downloadUrl && (
              <a href={downloadUrl} data-testid="download-btn"
                className="inline-block mb-2 px-8 py-3.5 bg-white text-black track-label hover:bg-white/80 transition-colors">
                DOWNLOAD YOUR MUSIC
              </a>
            )}
          </>
        )}

        {(state === "error" || state === "timeout") && (
          <>
            <XCircle size={40} className="mx-auto text-white/70 mb-5" />
            <h2 className="font-display steel-text text-2xl mb-3">{state === "timeout" ? "STILL PROCESSING" : "PAYMENT ISSUE"}</h2>
            <p className="font-body text-white/55 text-sm mb-8">{state === "timeout" ? "Your payment is taking a moment. Check your email for confirmation." : "We couldn\u2019t confirm the payment. No charge may have been made."}</p>
          </>
        )}

        {state === "cancel" && (
          <>
            <XCircle size={40} className="mx-auto text-white/50 mb-5" />
            <h2 className="font-display steel-text text-2xl mb-3">CHECKOUT CANCELLED</h2>
            <p className="font-body text-white/55 text-sm mb-8">No charge was made. The music is still waiting for you.</p>
          </>
        )}

        <button
          onClick={goHome}
          data-testid="payment-return-home"
          className="px-8 py-3.5 bg-white text-black track-label hover:bg-white/80 transition-colors"
        >
          RETURN TO THE WORLD
        </button>
      </motion.div>
    </div>
  );
}
